import { useState, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, type LucideIcon } from "lucide-react";

interface BuilderSectionProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  stepNumber?: number;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "outline" | "destructive";
  defaultOpen?: boolean;
  disabled?: boolean;
  headerActions?: ReactNode;
  children: ReactNode;
}

export function BuilderSection({
  title,
  description,
  icon: Icon,
  stepNumber,
  badge,
  badgeVariant = "secondary",
  defaultOpen = true,
  disabled,
  headerActions,
  children,
}: BuilderSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className={disabled ? "opacity-60 pointer-events-none" : ""}>
        <CollapsibleTrigger asChild>
          <div
            className="flex items-center gap-3 p-4 cursor-pointer select-none"
            data-testid={`section-trigger-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {stepNumber ? (
              <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
                {stepNumber}
              </div>
            ) : Icon ? (
              <div className="p-1.5 rounded-md bg-muted shrink-0">
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
            ) : null}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-medium text-foreground">
                  {title}
                </h2>
                {badge && (
                  <Badge
                    variant={badgeVariant}
                    className="no-default-hover-elevate no-default-active-elevate"
                  >
                    {badge}
                  </Badge>
                )}
              </div>
              {description && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {description}
                </p>
              )}
            </div>
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {headerActions}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              tabIndex={-1}
              data-testid={`button-toggle-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </Button>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="px-4 pb-4 pt-0 space-y-4">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
